import { useQuery } from '@tanstack/react-query';
import { Compass, Gauge } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { adminApi } from '@/api/endpoints/admin';
import { formatDateTime } from '@/lib/utils';

/**
 * Contador (só leitura) de features pagas liberadas por revenda para usuários finais,
 * ex.: Navegador Premium. Serve para o admin cobrar a revenda. Fonte: /admin/feature-usage.
 */
export function FeatureUsagePage() {
  const q = useQuery({ queryKey: ['admin', 'feature-usage'], queryFn: () => adminApi.featureUsage() });
  const data = q.data ?? [];
  const totalGeral = data.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-bold">Uso de features pagas</h1>
        <p className="text-sm text-muted-foreground">
          Liberações por revenda (ex.: Navegador Premium liberado para usuários finais). Base para a cobrança da revenda.
        </p>
      </div>

      {q.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando…</p>
      ) : q.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar (acesso restrito ao administrador).</p>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            Nenhuma liberação de feature paga ainda.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <Gauge className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total de liberações ativas</p>
                <p className="text-lg font-bold">{totalGeral}</p>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {data.map((rev) => (
              <Card key={rev.revenda_id ?? 'sem-revenda'}>
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold">{rev.revenda_nome}</p>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {rev.total} {rev.total === 1 ? 'liberação' : 'liberações'}
                    </span>
                  </div>
                  {rev.features.map((f) => (
                    <div key={f.key} className="rounded-md border border-border p-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Compass className="h-4 w-4 text-muted-foreground" />
                        {f.nome} <span className="text-muted-foreground">· {f.count}</span>
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {f.usuarios.map((u) => (
                          <li key={u.user_id} className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                            <span>usuário #{u.user_id}</span>
                            {u.liberado_por && <span>· por {u.liberado_por}</span>}
                            {u.liberado_em && <span>· {formatDateTime(u.liberado_em)}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
